import { useAuthContext } from '@/components/Providers/AuthProvider';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';
import { MetaBaseStrapi } from '@/types/MetaData';

import Dropdown from '../../DropdownMenu';
import MenuBar from '../MenuBar';

const defaultIconFont = '!v2-text-[1rem] v2-font-circular-std-bold';
const navItemClassName = '!v2-text-black !v2-font-normal !v2-text-xs !v2-font-sweet-sans-pro';

export default function ReplicatedDesktopMenu({
  dogCategories,
  dogIssues,
  catCategories,
  catIssues,
  ourStories,
  aboutUs,
  blogUrl,
  ingredientUrl,
  ezShipManagerPath,
  petProfilePath,
}: {
  dogCategories: MetaBaseStrapi[];
  dogIssues: MetaBaseStrapi[];
  catCategories: MetaBaseStrapi[];
  catIssues: MetaBaseStrapi[];
  ourStories: CustomLink[];
  aboutUs: CustomLink[];
  blogUrl: string;
  ingredientUrl: string;
  petProfilePath: string;
  ezShipManagerPath: string;
}) {
  const { webalias, generateURL } = useWebAlias();
  const generateApproperiateURL = generateURL;
  const { customPages } = useAuthContext();
  const isUserAuthorized = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);

  return (
    <>
      <Dropdown label="Shop Dogs">
        <Dropdown.TwoColumnLayout>
          <Dropdown.TwoColumnLayout.Column headline="Shop by product">
            {dogCategories.map(category => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                className={cn('v2-pb-2', {
                  'v2-text-primary': category.id === Infinity,
                })}
                labelClassName={cn(navItemClassName, {
                  '!v2-font-normal !v2-text-primary-900': category.id === Infinity,
                })}
                key={category.Name}
                iconClassName={cn(defaultIconFont, {
                  '!v2-text-[19px]': category.id === Infinity,
                })}
                href={generateApproperiateURL(
                  `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                  webalias
                )}
                label={category.Name}
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>

          <Dropdown.TwoColumnLayout.Column headline="Shop by Issue">
            {dogIssues.map(issue => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                className={cn('v2-pb-2', {
                  'v2-text-primary ': issue.id === Infinity,
                })}
                iconClassName={cn(navItemClassName, {
                  '!v2-text-[19px]': issue.id === Infinity,
                })}
                labelClassName={cn(navItemClassName, {
                  '!v2-font-normal !v2-text-primary-900': issue.id === Infinity,
                })}
                key={issue.Name}
                href={generateApproperiateURL(
                  `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                  webalias
                )}
                label={issue.Name}
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>
        </Dropdown.TwoColumnLayout>
      </Dropdown>

      <Dropdown label="Shop Cats">
        <Dropdown.TwoColumnLayout>
          <Dropdown.TwoColumnLayout.Column headline="Shop by product">
            {catCategories.map(category => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                key={category.Name}
                className={cn('v2-pb-2', { 'v2-text-primary': category.id === Infinity })}
                iconClassName={cn(defaultIconFont, {
                  '!v2-text-[19px]': category.id === Infinity,
                })}
                labelClassName={cn(navItemClassName, {
                  '!v2-font-normal !v2-text-primary-900': category.id === Infinity,
                })}
                href={generateApproperiateURL(
                  `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                  webalias
                )}
                label={category.Name}
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>

          <Dropdown.TwoColumnLayout.Column headline="Shop by Issue">
            {catIssues.map(issue => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                key={issue.Name}
                className={cn('v2-pb-2', { 'v2-text-primary v2-pb-2': issue.id === Infinity })}
                iconClassName={cn(defaultIconFont, {
                  '!v2-text-[19px]': issue.id === Infinity,
                })}
                labelClassName={cn(navItemClassName, {
                  '!v2-font-normal !v2-text-primary-900': issue.id === Infinity,
                })}
                href={generateApproperiateURL(
                  `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                  webalias
                )}
                label={issue.Name}
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>
        </Dropdown.TwoColumnLayout>
      </Dropdown>

      <Dropdown label="About Us">
        <Dropdown.SingleColumnLayout>
          {aboutUs.map(({ name, link }) => (
            <Dropdown.SingleColumnLayout.ColumnItem
              as={'a'}
              key={name}
              label={name}
              href={link}
              labelClassName={navItemClassName}
              className="v2-pb-2"
            />
          ))}
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      <Dropdown label="Resources">
        <Dropdown.SingleColumnLayout>
          <Dropdown.SingleColumnLayout.ColumnItem
            as="a"
            key="blog"
            label="Blog"
            href={blogUrl}
            labelClassName={navItemClassName}
            className="v2-pb-2"
          />
          <Dropdown.SingleColumnLayout.ColumnItem
            as="a"
            key="ingredient-101"
            label="Ingredients 101"
            href={ingredientUrl}
            labelClassName={navItemClassName}
            className="v2-pb-2"
          />
        </Dropdown.SingleColumnLayout>
      </Dropdown>

      <MenuBar.Link href={petProfilePath}>Pet Quiz</MenuBar.Link>

      {customPages && customPages?.length > 0 && (
        <Dropdown label="My Recommendations">
          <Dropdown.SingleColumnLayout>
            {(customPages || [])?.map(({ name, seoUrl }) => (
              <Dropdown.SingleColumnLayout.ColumnItem
                key={seoUrl}
                href={generateURL(`/myrecommendation/${encodeURIComponent(seoUrl)}`, webalias)}
                label={name}
                as={'a'}
                labelClassName={navItemClassName}
                className="v2-pb-2"
              />
            ))}
          </Dropdown.SingleColumnLayout>
        </Dropdown>
      )}
      {isUserAuthorized && <MenuBar.Link href={generateURL(ezShipManagerPath, webalias)}>EZ Ship Manager</MenuBar.Link>}
    </>
  );
}
